import React, { useState, useEffect } from 'react';
import {
  CreditCard,
  Calendar,
  DollarSign,
  CheckCircle,
  XCircle,
  AlertCircle,
  Clock,
  ArrowRight,
  RefreshCw,
  ExternalLink,
  Package,
  Users,
  Building2,
  Zap,
  Info
} from 'lucide-react';
import { tenantPlanService, TenantPlan } from '../../services/tenantPlan.service';
import { planService, Plan } from '../../services/plan.service';
import toast from 'react-hot-toast';
import { useSearchParams } from 'react-router-dom';

export const PlanSubscription: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPlan, setCurrentPlan] = useState<TenantPlan | null>(null);
  const [planHistory, setPlanHistory] = useState<TenantPlan[]>([]);
  const [availablePlans, setAvailablePlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [renewingPlan, setRenewingPlan] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState('');

  useEffect(() => {
    // Check if we're returning from Stripe checkout
    const wasCheckoutInitiated = sessionStorage.getItem('stripe_checkout_initiated');
    
    // Handle Stripe redirect status
    const status = searchParams.get('status');
    const sessionId = searchParams.get('session_id');
    
    if (wasCheckoutInitiated === 'true' && status) {
      console.log('✅ Returning from Stripe checkout, status:', status);
      
      if (status === 'success') {
        toast.success('Payment successful! Your plan has been updated.', {
          duration: 5000,
          icon: '🎉'
        });
      } else if (status === 'cancelled') {
        toast.error('Payment was cancelled. No changes were made to your plan.', {
          duration: 5000
        });
      }
      
      // Clear the flags and URL parameters
      sessionStorage.removeItem('stripe_checkout_initiated');
      sessionStorage.removeItem('stripe_return_url');
      
      // Clear URL parameters after showing toast
      setTimeout(() => {
        setSearchParams({});
      }, 500);
    }
    
    // Load plan data
    loadPlanData();
  }, []);

  const loadPlanData = async () => {
    try {
      setLoading(true);
      
      // Load current plan
      const currentResponse = await tenantPlanService.getCurrentPlan();
      setCurrentPlan(currentResponse.data);

      // Load available plans for upgrade
      const plansResponse = await planService.getPlansForPurchase();
      setAvailablePlans(plansResponse.data);

      // Load plan history (if current plan exists, we have tenantId)
      if (currentResponse.data) {
        try {
          const historyResponse = await tenantPlanService.getTenantPlanHistory(currentResponse.data.tenantId);
          setPlanHistory(historyResponse.data);
        } catch (err) {
          console.error('Error loading plan history:', err);
        }
      }
    } catch (error: any) {
      console.error('Error loading plan data:', error);
      toast.error('Failed to load subscription information');
    } finally {
      setLoading(false);
    }
  };

  const handleRenewPlan = async () => {
    if (!currentPlan) return;

    try {
      setRenewingPlan(true);
      await tenantPlanService.renewTenantPlan(currentPlan._id);
      toast.success('Plan renewed successfully');
      loadPlanData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to renew plan');
    } finally {
      setRenewingPlan(false);
    }
  };

  const handleUpgradePlan = async () => {
    if (!selectedPlanId) {
      toast.error('Please select a plan');
      return;
    }

    // Check if selected plan supports online payment
    const selectedPlan = availablePlans.find(p => p._id === selectedPlanId);
    if (selectedPlan && !selectedPlan.stripePriceId) {
      toast.error('This plan does not support online payment. Please contact support for assistance.', {
        duration: 5000
      });
      return;
    }

    try {
      const result = await tenantPlanService.purchasePlan({ planId: selectedPlanId });
      
      if (result.checkoutUrl) {
        // Store flag and auth backup in sessionStorage to indicate we're going to Stripe
        const currentUrl = window.location.href;
        sessionStorage.setItem('stripe_checkout_initiated', 'true');
        sessionStorage.setItem('stripe_return_url', currentUrl);
        console.log('✅ SAVED return URL to sessionStorage:', currentUrl);
        console.log('✅ Verification - reading back:', sessionStorage.getItem('stripe_return_url'));
        
        // Backup auth data to sessionStorage (survives redirects better than localStorage in some browsers)
        const authData = localStorage.getItem('erpca-auth');
        if (authData) {
          sessionStorage.setItem('erpca-auth-backup', authData);
          console.log('💾 Auth data backed up to sessionStorage before Stripe redirect');
        }
        
        // Redirect to Stripe checkout
        toast.success('Redirecting to payment gateway...', { duration: 2000 });
        setTimeout(() => {
          window.location.href = result.checkoutUrl;
        }, 1000);
      } else {
        toast.success('Plan upgraded successfully');
        setShowUpgradeModal(false);
        loadPlanData();
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to upgrade plan';
      toast.error(errorMessage, {
        duration: 5000
      });
      console.error('Plan upgrade error:', error);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      active: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Active' },
      trial: { color: 'bg-blue-100 text-blue-800', icon: Clock, label: 'Trial' },
      expired: { color: 'bg-gray-100 text-gray-800', icon: XCircle, label: 'Expired' },
      suspended: { color: 'bg-red-100 text-red-800', icon: AlertCircle, label: 'Suspended' },
      cancelled: { color: 'bg-orange-100 text-orange-800', icon: XCircle, label: 'Cancelled' },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.expired;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
        <Icon className="w-4 h-4 mr-1" />
        {config.label}
      </span>
    );
  };

  const getDaysRemaining = (endDate: string) => {
    const end = new Date(endDate);
    const now = new Date();
    const diffTime = end.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-8 border border-gray-200">
        <div className="flex items-center justify-center">
          <RefreshCw className="w-6 h-6 text-primary-600 animate-spin mr-3" />
          <span className="text-gray-600">Loading subscription information...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Current Plan */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-4">
          <h2 className="text-xl font-semibold text-white flex items-center">
            <Package className="w-6 h-6 mr-2" />
            Current Subscription Plan
          </h2>
        </div>

        {currentPlan ? (
          <div className="p-6">
            <div className="flex items-start justify-between mb-6">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <h3 className="text-2xl font-bold text-gray-900">{currentPlan.planName}</h3>
                  {getStatusBadge(currentPlan.status)}
                </div>
                <p className="text-gray-600 mb-4">
                  Your current subscription plan with all the features and benefits
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center text-gray-600 mb-1">
                      <DollarSign className="w-4 h-4 mr-1" />
                      <span className="text-sm">Cost</span>
                    </div>
                    <p className="text-2xl font-bold text-gray-900">
                      ${currentPlan.planCost}
                      <span className="text-sm font-normal text-gray-600"> /{currentPlan.planDuration}</span>
                    </p>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center text-gray-600 mb-1">
                      <Calendar className="w-4 h-4 mr-1" />
                      <span className="text-sm">Plan Period</span>
                    </div>
                    <p className="text-sm font-medium text-gray-900">
                      {new Date(currentPlan.startDate).toLocaleDateString()} - {new Date(currentPlan.endDate).toLocaleDateString()}
                    </p>
                    {currentPlan.status === 'active' && (
                      <p className="text-xs text-gray-600 mt-1">
                        {getDaysRemaining(currentPlan.endDate)} days remaining
                      </p>
                    )}
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center text-gray-600 mb-1">
                      <CreditCard className="w-4 h-4 mr-1" />
                      <span className="text-sm">Payment</span>
                    </div>
                    <p className="text-sm font-medium text-gray-900 capitalize">
                      {currentPlan.paymentMethod}
                    </p>
                    <p className="text-xs text-gray-600 mt-1 capitalize">
                      Status: {currentPlan.paymentStatus}
                    </p>
                  </div>
                </div>

                {/* Plan Expiry Warning */}
                {currentPlan.status === 'active' && getDaysRemaining(currentPlan.endDate) <= 7 && (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                    <div className="flex items-start">
                      <AlertCircle className="w-5 h-5 text-orange-500 mr-3 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="text-sm font-medium text-orange-900 mb-1">
                          Plan Expiring Soon
                        </h4>
                        <p className="text-sm text-orange-700">
                          Your plan will expire in {getDaysRemaining(currentPlan.endDate)} days. 
                          {currentPlan.autoRenew ? ' Auto-renewal is enabled.' : ' Please renew to continue using our services.'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-3">
                  {currentPlan.status === 'active' && !currentPlan.autoRenew && (
                    <button
                      onClick={handleRenewPlan}
                      disabled={renewingPlan}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {renewingPlan ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Renewing...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Renew Plan
                        </>
                      )}
                    </button>
                  )}

                  <button
                    onClick={() => setShowUpgradeModal(true)}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    <Zap className="w-4 h-4 mr-2" />
                    Upgrade Plan
                  </button>
                </div>
              </div>
            </div>

            {/* Additional Info */}
            {currentPlan.notes && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start">
                  <Info className="w-5 h-5 text-blue-500 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-medium text-blue-900 mb-1">Notes</h4>
                    <p className="text-sm text-blue-700">{currentPlan.notes}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="p-8 text-center">
            <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Active Plan</h3>
            <p className="text-gray-600 mb-4">You don't have an active subscription plan.</p>
            <button
              onClick={() => setShowUpgradeModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <Zap className="w-4 h-4 mr-2" />
              Choose a Plan
            </button>
          </div>
        )}
      </div>

      {/* Plan History */}
      {planHistory.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <Clock className="w-5 h-5 mr-2 text-gray-600" />
              Subscription History
            </h2>
          </div>

          <div className="p-6">
            <div className="space-y-3">
              {planHistory.map((plan) => (
                <div
                  key={plan._id}
                  className={`border rounded-lg p-4 ${
                    plan.status === 'active' ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <h4 className="text-sm font-medium text-gray-900">{plan.planName}</h4>
                        {getStatusBadge(plan.status)}
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs text-gray-600">
                        <div>
                          <span className="font-medium">Period:</span>{' '}
                          {new Date(plan.startDate).toLocaleDateString()} - {new Date(plan.endDate).toLocaleDateString()}
                        </div>
                        <div>
                          <span className="font-medium">Cost:</span> ${plan.planCost} {plan.planCurrency}
                        </div>
                        <div>
                          <span className="font-medium">Duration:</span> {plan.planDuration}
                        </div>
                        <div>
                          <span className="font-medium">Payment:</span> {plan.paymentMethod}
                        </div>
                      </div>
                      {plan.notes && (
                        <p className="mt-2 text-xs text-gray-600 italic">{plan.notes}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Choose Your Plan</h2>
              <button
                onClick={() => setShowUpgradeModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {availablePlans.map((plan) => (
                  <div
                    key={plan._id}
                    onClick={() => setSelectedPlanId(plan._id)}
                    className={`border-2 rounded-lg p-6 cursor-pointer transition-all ${
                      selectedPlanId === plan._id
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">{plan.displayName}</h3>
                        <p className="text-sm text-gray-600 mt-1">{plan.description}</p>
                      </div>
                      {selectedPlanId === plan._id && (
                        <CheckCircle className="w-6 h-6 text-primary-600" />
                      )}
                    </div>

                    <div className="mb-4">
                      <span className="text-3xl font-bold text-gray-900">${plan.cost}</span>
                      <span className="text-gray-600"> /{plan.duration}</span>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center text-sm text-gray-600">
                        <Users className="w-4 h-4 mr-2" />
                        Up to {plan.maxUsers} users
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <Building2 className="w-4 h-4 mr-2" />
                        Up to {plan.maxClients} clients
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <Zap className="w-4 h-4 mr-2" />
                        {plan.features.length} features included
                      </div>
                    </div>

                    {!plan.stripePriceId && (
                      <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-700">
                        <AlertCircle className="w-3 h-3 inline mr-1" />
                        Online payment not available. Contact support.
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowUpgradeModal(false)}
                  className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpgradePlan}
                  disabled={!selectedPlanId}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  title={selectedPlanId && !availablePlans.find(p => p._id === selectedPlanId)?.stripePriceId ? 'This plan does not support online payment' : ''}
                >
                  Continue to Payment
                  <ArrowRight className="w-4 h-4 ml-2" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
